import { Box, Grid, makeStyles, Tooltip } from "@material-ui/core";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import { useEffect, useMemo, useState } from "react";
import CssPagination from "src/components/Pagination/CssPagination";
import MultipleSelector from "src/components/Selector/MultipleSelector";
import SearchTextField from "src/components/TextField/SearchTextField";
import PaperWrapper from "src/components/Wrapper/PaperWrapper";
import { useTokenAnalysisSelector } from "src/context/tokenAnalysisContext";
import usePagination from "src/hooks/usePagination";
import useSearchData from "src/hooks/useSearchData";
import { getFavoriteTokens } from "src/services/favoriteToken";
import TokenComparisonHealthChart from "./TokenComparisonHealthChart";
import TokenComparisonHistoryChart from "./TokenComparisonHistoryChart";

const useStyle = makeStyles((theme) => ({
  multipleSelect: {
    [theme.breakpoints.down("sm")]: {
      marginBottom: "1.5rem",
    },
  },
  grid: {
    paddingTop: "1rem",
    minHeight: "470px",
  },
  historyChart: {
    [theme.breakpoints.down("sm")]: {
      marginTop: "1.5rem",
    },
  },
}));

const searchToken = (element, index, searchText) => {
  return element.label.toString().toLowerCase().includes(searchText.toString().toLowerCase());
};

export default function TokenComparison() {
  const cls = useStyle();
  const [searchText, setSearchText] = useState("");
  const [selectedData, setSelectedData] = useState([]);
  const [showFavoriteOnly, setSowFavoriteOnly] = useState(false);

  const tokens = useTokenAnalysisSelector((state) => state.tokens);

  const tokenList = useMemo(() => {
    if (tokens) {
      let _tokens = tokens;
      if (showFavoriteOnly) {
        const favoriteToken = getFavoriteTokens();
        _tokens = _tokens.filter((element) => {
          return favoriteToken.includes(element.token_id);
        });
      }
      return _tokens.map((element) => {
        return { label: element.token_id };
      });
    } else return [];
  }, [tokens, showFavoriteOnly]);

  useEffect(() => {
    const favoriteToken = getFavoriteTokens();
    const initSelectedData = favoriteToken.slice(0, 5).map((element) => {
      return { label: element };
    });
    setSelectedData(initSelectedData);
  }, []);

  const searchData = useSearchData(tokenList, searchText, searchToken);
  const { data, jump, maxPage, currentPage } = usePagination(searchData);

  function onChooseItem(event, item, selectedItems) {
    setSelectedData(selectedItems);
  }

  function onRemoveItem(event, item, selectedItems) {
    setSelectedData(selectedItems);
  }

  return (
    <PaperWrapper title="Token Comparison" style={{ paddingTop: "2rem" }}>
      <MultipleSelector
        items={data}
        defaultSelectedItems={selectedData}
        event={{
          onChooseItem: (event, item, selectedItems) => onChooseItem(event, item, selectedItems),
          onRemoveItem: (event, item, selectedItems) => onRemoveItem(event, item, selectedItems),
        }}
        config={{ maxItem: 5 }}
        Component={{
          start: (
            <Box px={2}>
              <SearchTextField
                fullWidth
                variant="outlined"
                placeholder="Search tokens"
                value={searchText}
                event={{ onChange: (value) => setSearchText(value), onClear: () => setSearchText("") }}
              />
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex">
                  {showFavoriteOnly ? (
                    <Tooltip title="Show all tokens" placement="top">
                      <CheckBoxIcon style={{ cursor: "pointer" }} onClick={() => setSowFavoriteOnly(false)} />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Show your tokens" placement="top">
                      <CheckBoxOutlineBlankIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => setSowFavoriteOnly(true)}
                      />
                    </Tooltip>
                  )}
                </Box>
                <CssPagination
                  fullWidth
                  mt={1}
                  paginationProps={{ page: currentPage, count: maxPage, onChange: (_, page) => jump(page) }}
                />
              </Box>
            </Box>
          ),
        }}
      />
      {selectedData.length > 0 && (
        <Grid container className={cls.grid}>
          <Grid item md={6} xs={12}>
            <TokenComparisonHealthChart tokenIds={selectedData.map((item) => item.label)} />
          </Grid>
          <Grid item md={6} xs={12} className={cls.historyChart}>
            <TokenComparisonHistoryChart tokenIds={selectedData.map((item) => item.label)} />
          </Grid>
        </Grid>
      )}
    </PaperWrapper>
  );
}
